import type { AccessModule, PrintableKind } from "@bridewell/shared";

/**
 * Every TanStack Query key, in one place. Everything derived from one wedding nests under `["wedding", id]`, so one
 * invalidation reaches all of it; everything about the signed-in person nests under `["me"]`. Keys whose second part is
 * a bearer token (`invite`, `rsvp`, `share`) or that are public (`public`) start with their own word, so a client can
 * keep them out of persisted caches by the first element alone.
 */
export const keys = {
  me: ["me"] as const,
  myWeddings: ["me", "weddings"] as const,
  usage: ["me", "usage"] as const,
  planner: ["me", "planner"] as const,
  templates: ["me", "templates"] as const,
  notifications: ["me", "notifications"] as const,
  sessions: ["me", "sessions"] as const,
  /** The account's own sign-in methods, read through each platform's Better Auth client (`listAccounts`). */
  linkedAccounts: ["me", "accounts"] as const,
  billing: ["me", "billing"] as const,
  billingPlans: ["me", "billing", "plans"] as const,
  playBilling: ["me", "billing", "google"] as const,
  publicPlans: ["public", "billing", "plans"] as const,
  signInMethods: ["public", "sign-in-methods"] as const,
  invite: (token: string) => ["invite", token] as const,
  rsvpPublic: (token: string) => ["rsvp", token] as const,
  runSheet: (token: string, passcode: string) => ["share", token, passcode] as const,
  wedding: (id: string) => ["wedding", id] as const,
  dashboard: (id: string) => ["wedding", id, "dashboard"] as const,
  module: (id: string, resource: string) => ["wedding", id, "module", resource] as const,
  headroom: (id: string, resource: string) => ["wedding", id, "module", resource, "headroom"] as const,
  members: (id: string) => ["wedding", id, "members"] as const,
  invites: (id: string) => ["wedding", id, "invites"] as const,
  activity: (id: string) => ["wedding", id, "activity"] as const,
  payments: (id: string) => ["wedding", id, "payments"] as const,
  insights: (id: string) => ["wedding", id, "insights"] as const,
  printable: (id: string, kind: PrintableKind) => ["wedding", id, "printable", kind] as const,
  seatingRules: (id: string) => ["wedding", id, "seating-rules"] as const,
  guestMail: (id: string) => ["wedding", id, "guest-mail"] as const,
  rsvp: (id: string) => ["wedding", id, "rsvp"] as const,
  shareLinks: (id: string) => ["wedding", id, "share-links"] as const,
};

/** The API path of each list module (`/weddings/:id/<path>`). The routes are the contract: changing one is an API change. */
export const RESOURCE: Record<Exclude<AccessModule, "overview" | "activity">, string> = {
  guests: "guests",
  budget: "budget-items",
  checklist: "checklist-items",
  shopping: "shopping-items",
  notes: "notes",
  inspiration: "inspiration-photos",
  vendors: "vendors",
  timeline: "timeline-events",
  registry: "registry-items",
  seating: "seating-tables",
};
export type ResourceModule = keyof typeof RESOURCE;
